import { FormikErrors, FormikProps } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  IRangeOption,
  ISocreStrategyOption,
  ISurvey,
  ISurveyDisplayRules,
  ISurveySection,
} from "../../../@types/Surveys";
import { FixedTranslationsLanguageCodes } from "../../../@types/Translations";
import { IOption } from "../../../components/SelectDropdown";
import { GraphTypes } from "../../../constants/surveys";
import { useToast } from "../../../hooks/useToast";
import {
  BuilderLayout,
  ISectionButton,
} from "../../../layout/components/BuilderLayout/BuilderLayout";
import { surveysService } from "../../../services/surveysService";
import { translationService } from "../../../services/translationsService";
import {
  isErrorResponse,
  extractErrors,
  getSummaryErrorMessage,
} from "../../../utils/apiErrorUtils";
import { getTranslatedSurveyOptions } from "../../../utils/surveyOptionLabels";
import { parseSurveyTranslationResponseToDisplay } from "../../../utils/translationsUtils";
import { SurveyBuilderContentTab } from "./Components/ContentTab";
import { DisplaySettingsFormik } from "./Components/DisplayTab";
import { BodyContainer, ScrollableContainer } from "./SurveyBuilder.styles";

type sectionType = "display" | "content";

export type LayoutStateValues = {
  Sections: ISurveySection[];
  SurveyDescription: string;
  SurveyTitle: string;
  SurveyCode: string;
  Domains: string[];
  HasScoring: boolean;
  ScoreStrategy: string;
  SurveyType: string;
};

export interface IDisplayRulesOptions {
  displayRulesTypes: IOption[];
  endComparators: IOption[];
  startComparators: IOption[];
}

const extractFormikErrors = (
  errors: FormikErrors<ISurveyDisplayRules>
): string[] => {
  const errorMessages: string[] = [];
  const seenMessages = new Set<string>();

  // Internal validation messages that shouldn't be shown to users
  const internalMessages = ["An array is required"];

  const addUniqueError = (message: string) => {
    if (!seenMessages.has(message) && !internalMessages.includes(message)) {
      seenMessages.add(message);
      errorMessages.push(message);
    }
  };

  const extractFromValue = (value: unknown): void => {
    if (typeof value === "string") {
      addUniqueError(value);
    } else if (Array.isArray(value)) {
      value.forEach((item) => extractFromValue(item));
    } else if (value && typeof value === "object") {
      Object.values(value).forEach((v) => extractFromValue(v));
    }
  };

  extractFromValue(errors);
  return errorMessages;
};

export function SurveyBuilder() {
  const { t, i18n } = useTranslation();
  const [loadingTranslations, setLoadingTranslations] =
    useState<boolean>(false);
  const [loadingSurvey, setLoadingSurvey] = useState<boolean>(false);
  const [loadingDomains, setLoadingDomains] = useState<boolean>(true);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [surveyTypeError, setSurveyTypeError] = useState<string>("");
  const [scoreStrategyError, setScoreStrategyError] = useState<string>("");

  const { showToast } = useToast();

  const [selectedSection, setSelectedSection] =
    useState<sectionType>("content");
  const [surveyTitle, setSurveyTitle] = useState<string>(
    t("surveys_builder_titlePlaceholder")
  );
  const [surveyDescription, setSurveyDescription] = useState<string>(
    t("surveys_builder_descriptionPlaceholder")
  );
  const [surveyType, setSurveyType] = useState<string>();
  const [surveyCode, setSurveyCode] = useState<string>();
  const [sections, setSections] = useState<ISurveySection[]>([
    {
      order: 1,
      title: t("surveys_builder_sectionTitlePlaceholder"),
      questions: [],
    },
  ]);
  const [scoreStrategy, setScoreStrategy] = useState<string>("");
  const [hasScoring, setHasScoring] = useState(false);
  const [domains, setDomains] = useState<string[]>([]);
  const [displayRules, setDisplayRules] = useState<ISurveyDisplayRules>({
    type: GraphTypes.tableView,
  });
  const [scoreStrategiesOptions, setScoreStrategiesOptions] = useState<
    IOption[]
  >([]);
  const [displayRulesOptions, setDisplayRulesOptions] =
    useState<IDisplayRulesOptions>({
      displayRulesTypes: [],
      endComparators: [],
      startComparators: [],
    });
  const [surveyTypeOptions, setSurveyTypeOptions] = useState<IOption[]>([]);

  // EN-5285: labels are resolved at render so they follow the published translations, which
  // can land after the options were fetched.
  const translatedSurveyTypeOptions = useMemo(
    () => getTranslatedSurveyOptions("surveyType", surveyTypeOptions, t),
    [surveyTypeOptions, t]
  );
  const translatedScoreStrategiesOptions = useMemo(
    () =>
      getTranslatedSurveyOptions("scoreStrategy", scoreStrategiesOptions, t),
    [scoreStrategiesOptions, t]
  );
  const translatedDisplayRulesOptions = useMemo(
    () => ({
      ...displayRulesOptions,
      displayRulesTypes: getTranslatedSurveyOptions(
        "displayRule",
        displayRulesOptions.displayRulesTypes,
        t
      ),
    }),
    [displayRulesOptions, t]
  );

  const actionRef = React.createRef<FormikProps<ISurveyDisplayRules>>();

  const navigate = useNavigate();
  const { surveyId } = useParams();

  const handleCancel = () => {
    navigate("/surveys");
  };

  const handleFlowSectionClick = async (section: string) => {
    if (section === "content") {
      setSelectedSection("content");
    } else {
      setSelectedSection("display");
    }
  };

  const sectionButtons: ISectionButton[] = [
    {
      isSelected: selectedSection === "content",
      onButtonClick: handleFlowSectionClick,
      label: t("surveys_builder_tabs_content"),
      name: "content",
    },
    {
      isSelected: selectedSection === "display",
      onButtonClick: handleFlowSectionClick,
      label: t("surveys_builder_tabs_display"),
      name: "display",
    },
  ];

  const getSurveyDisplaySettings = async () => {
    const [displayRulesResponse, comparatorsResponse] = await Promise.all([
      surveysService.getSurveyDisplayRules(),
      surveysService.getSurveyDisplayRulesComparators(),
    ]);

    const displayRulesTypes = displayRulesResponse.data.map((displayRule) => ({
      label: displayRule.name,
      value: displayRule.type,
      key: uuidv4(),
    }));

    const endComparators = comparatorsResponse.data
      .filter((comparator) => comparator.includes("<") && comparator !== "=")
      .map((comparator) => ({
        label: comparator,
        value: comparator,
        key: uuidv4(),
      }));

    const startComparators = comparatorsResponse.data
      .filter((comparator) => !comparator.includes("<"))
      .map((comparator) => ({
        label: comparator,
        value: comparator,
        key: uuidv4(),
      }));

    setDisplayRulesOptions({
      displayRulesTypes,
      endComparators,
      startComparators,
    });
  };

  useEffect(() => {
    i18n.changeLanguage();
    getSurveyDisplaySettings();
    if (surveyId !== undefined) {
      setLoadingTranslations(true);
      setLoadingSurvey(true);
      (async () => {
        const survey: ISurvey = (await surveysService.getSurveyById(surveyId))
          .data;
        setSurveyTitle(survey.name);
        setSurveyDescription(survey.description ?? "");
        setSurveyType(survey.type);
        if (survey.code) {
          setSurveyCode(survey.code);
        }
        setSections(survey.sections);
        setHasScoring(
          survey.scoreStrategy !== "" && survey.scoreStrategy !== undefined
        );
        setScoreStrategy(survey.scoreStrategy ?? "");

        if (survey.displayRules) {
          if (survey.displayRules.ranges && survey.displayRules.ranges.length) {
            survey.displayRules.ranges.sort(
              (a: IRangeOption, b: IRangeOption) => a.start - b.start
            );
          }

          setDisplayRules(survey.displayRules);
        }

        const savedDomains = new Set<string>();

        survey.sections.forEach((section) => {
          section.questions.forEach((question) => {
            if (question.domain === undefined) return;

            savedDomains.add(question.domain);
          });
        });

        setDomains(Array.from(savedDomains));
        setLoadingSurvey(false);
      })();
    }

    (async () => {
      const surveyTypes = (await surveysService.getSurveyTypesOptions()).data;

      setSurveyTypeOptions(
        surveyTypes.map((type) => {
          return { value: type, label: type, key: uuidv4() };
        })
      );

      const scoreStrategiesOptions: ISocreStrategyOption[] = (
        await surveysService.getScoreStrategiesOptions()
      ).data;

      const strategyOptionsToAdd: IOption[] = scoreStrategiesOptions.map(
        (scoreStrategy) => {
          return {
            value: scoreStrategy.type,
            label: scoreStrategy.name,
            key: scoreStrategy.type,
          };
        }
      );

      setScoreStrategiesOptions(strategyOptionsToAdd);
      setLoadingDomains(false);
    })();

    translationService
      .getSurveysFixedTranslationsByLanguageCode(
        i18n.language as FixedTranslationsLanguageCodes
      )
      .then((translations) => {
        if (!translations.data) return;
        const resourcesToAdd = parseSurveyTranslationResponseToDisplay(
          translations.data
        );
        i18n.addResourceBundle(i18n.language, "translation", resourcesToAdd);
        setLoadingTranslations(false);
      });
  }, []);

  const parseSectionsToSubmit = () => {
    const updatedSections = [...sections];
    let questionOrder = 0;

    sections.forEach((section, sectionIndex) => {
      updatedSections[sectionIndex].title = section.title.trim();

      section.questions.forEach((question, questionIndex) => {
        questionOrder += 1;
        updatedSections[sectionIndex].questions[questionIndex].order =
          questionOrder;
        updatedSections[sectionIndex].questions[questionIndex].text =
          question.text.trim();
        updatedSections[sectionIndex].questions[questionIndex].description =
          question.description.trim();

        if (question.attributes) {
          const attributesUpdate = question.attributes.filter(
            (attribute) =>
              !(attribute.name === "Intervals" && attribute.value === "")
          );
          updatedSections[sectionIndex].questions[questionIndex].attributes =
            attributesUpdate;
        }
      });
    });

    return updatedSections;
  };

  const handleSubmit = async () => {
    const contentTabErrors: string[] = [];

    // Validate survey type
    if (!surveyType) {
      setSurveyTypeError(t("surveys_builder_errors_surveyTypeRequired"));
      contentTabErrors.push(t("surveys_builder_errors_surveyTypeRequired"));
    } else {
      setSurveyTypeError("");
    }

    // Validate score strategy when scoring is enabled
    if (hasScoring && scoreStrategy === "") {
      setScoreStrategyError(t("surveys_builder_errors_scoreStrategyRequired"));
      contentTabErrors.push(t("surveys_builder_errors_scoreStrategyRequired"));
    } else {
      setScoreStrategyError("");
    }

    // Validate that at least one question exists
    const totalQuestions = sections.reduce(
      (count, section) => count + section.questions.length,
      0
    );
    if (totalQuestions === 0) {
      contentTabErrors.push(t("surveys_builder_errors_questionRequired"));
    }

    // Always trigger Display tab validation to show inline errors
    actionRef.current?.submitForm();
    const isDisplayValid = actionRef?.current?.isValid;

    // Check for Content tab errors
    if (contentTabErrors.length > 0) {
      const errorSummary = contentTabErrors.join("\n");
      const crossTabMessage =
        selectedSection !== "content"
          ? t("surveys_builder_errors_checkContentTab")
          : "";
      showToast(`${errorSummary}${crossTabMessage}`, "error");
      return;
    }

    // Check for Display tab errors
    if (!isDisplayValid && actionRef.current?.errors) {
      const displayTabErrors = extractFormikErrors(actionRef.current.errors);
      const errorSummary =
        displayTabErrors.length > 0
          ? displayTabErrors.join("\n")
          : t("surveys_builder_errors_displayRules");
      const crossTabMessage =
        selectedSection !== "display"
          ? t("surveys_builder_errors_checkDisplayTab")
          : "";
      showToast(`${errorSummary}${crossTabMessage}`, "error");
      return;
    }

    // Validate score strategy when graph display type is selected (non-table view)
    if (
      scoreStrategy === "" &&
      actionRef?.current?.values.type !== GraphTypes.tableView
    ) {
      const crossTabMessage =
        selectedSection !== "content"
          ? t("surveys_builder_errors_checkContentTab")
          : "";
      showToast(
        `${t(
          "surveys_builder_errors_scoreStrategyRequired"
        )}${crossTabMessage}`,
        "error"
      );
      return;
    }

    const updatedSections = parseSectionsToSubmit();
    const updatedDisplayRules = actionRef.current?.values;

    setLoadingSubmit(true);

    try {
      let response;

      if (!surveyId) {
        const finalSurvey: ISurvey = {
          name: surveyTitle.trim(),
          description: surveyDescription.trim(),
          type: surveyType,
          sections: updatedSections,
          scoreStrategy,
          code: surveyCode ?? undefined,
          displayRules: updatedDisplayRules,
        };

        response = await surveysService.addSurvey(finalSurvey);
      } else {
        const finalSurvey: ISurvey = {
          id: surveyId,
          name: surveyTitle.trim(),
          description: surveyDescription.trim(),
          type: surveyType,
          sections: updatedSections,
          scoreStrategy,
          code: surveyCode ?? undefined,
          displayRules: updatedDisplayRules,
        };

        response = await surveysService.editSurvey(finalSurvey);
      }

      if (isErrorResponse(response)) {
        const errors = extractErrors(response);
        showToast(getSummaryErrorMessage(errors), "error");
        setLoadingSubmit(false);
        return;
      }

      showToast(
        surveyId
          ? t("surveys_builder_toasts_updated")
          : t("surveys_builder_toasts_created"),
        "success"
      );
      navigate("/surveys");
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { errors?: Array<{ message?: string }> } };
      };
      const errorMessage = axiosError?.response?.data?.errors?.[0]?.message;
      showToast(
        errorMessage || t("surveys_builder_errors_saveFailed"),
        "error"
      );
      setLoadingSubmit(false);
    }
  };

  const updateSurveyStates = <K extends keyof LayoutStateValues>(
    state: K,
    value: LayoutStateValues[K]
  ) => {
    switch (state) {
      case "Sections":
        setSections(value as ISurveySection[]);
        break;
      case "SurveyDescription":
        setSurveyDescription(value as string);
        break;
      case "SurveyTitle":
        setSurveyTitle(value as string);
        break;
      case "Domains":
        setDomains(value as string[]);
        break;
      case "HasScoring":
        setHasScoring(value as boolean);
        if (!(value as boolean)) {
          setScoreStrategyError("");
        }
        break;
      case "ScoreStrategy":
        setScoreStrategy(value as string);
        setScoreStrategyError("");
        break;
      case "SurveyCode":
        setSurveyCode(value as string);
        break;
      case "SurveyType":
        setSurveyType(value as string);
        setSurveyTypeError("");
        break;
      default:
        break;
    }
  };

  const renderSurveyBuilderTabs = () => {
    return (
      <>
        <SurveyBuilderContentTab
          surveyId={surveyId}
          display={selectedSection === "content"}
          updateSurveyStates={updateSurveyStates}
          sections={sections}
          domains={domains}
          scoreStrategiesOptions={translatedScoreStrategiesOptions}
          hasScoring={hasScoring}
          scoreStrategy={scoreStrategy}
          surveyDescription={surveyDescription}
          surveyTitle={surveyTitle}
          surveyCode={surveyCode}
          surveyType={surveyType}
          surveyTypeOptions={translatedSurveyTypeOptions}
          surveyTypeError={surveyTypeError}
          scoreStrategyError={scoreStrategyError}
        />
        <ScrollableContainer
          style={{ display: selectedSection === "display" ? "" : "none" }}
        >
          <BodyContainer>
            <DisplaySettingsFormik
              sections={sections}
              ref={actionRef}
              displayRules={displayRules}
              displayRulesOptions={translatedDisplayRulesOptions}
              hasScoreStrategy={hasScoring}
              domains={domains}
            />
          </BodyContainer>
        </ScrollableContainer>
      </>
    );
  };

  return (
    <BuilderLayout
      handleOnSave={handleSubmit}
      handleOnCancel={handleCancel}
      isLayoutLoading={loadingTranslations || loadingDomains || loadingSurvey}
      location="Surveys"
      lastEdited={new Date().toString()}
      sectionButtons={sectionButtons}
      isWhiteButtonLoading={loadingSubmit}
    >
      {renderSurveyBuilderTabs()}
    </BuilderLayout>
  );
}
